import { navigate } from "@reach/router";
import { Button } from "newui/components/Button";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { Documentation } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Documentation";
import {
	getDocumentType,
	getRFQType,
	saveDraftRFQ,
} from "actions/RFQ/rfqsAction";
import { Form, Formik } from "formik";
import Quote from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Quote/Quote";
import { Suppliers } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Suppliers";
import { PreviewRFQ } from "../../../../pages/RFQ/RFQ/CreateRFQ/PreviewRFQ";
import {
	formValuesGenerator,
	getTabLists,
	validationSchema,
	validStepChecker,
} from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/createRFQUtils";
import TextButton from "newui/components/Button/TextButton";
import SubmitRFQModal from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/SubmitRFQModal";
import { NotFound } from "routes/NotFound";
import { showToast } from "utils/helpers";
import { RfxContext } from "newui/features/Rfx/context/RfxContext";
import useRefreshWarning from "hooks/useRefreshWarning";
import { RFQTabNavigation } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/RFQTabNavigation";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import ContinueButton from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/useContinueButton";
import { CreateRFxHeaderButtons } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/CreateRFxHeaderButtons";
import InvitePreApprovalDrawer from "components/Pages/RFQDetail/InvitePreApprovalDrawer/InvitePreApprovalDrawer";
import { RFX_STATUS } from "utils/constants";
import { logErrorIfDebug } from "utils/logging";
import SavingOverlay from "newui/components/SavingOverlay";
import ReturnableSchedule from "./components/ReturnableSchedule/ReturnableSchedule";
import {
	mergeCreateRFxFormData,
	processFormValuesForSaving,
} from "./data-processor";
import ProjectDetail from "./components/ProjectDetail";
import { useRfxSubmissionData } from "../data-provider/RfxSubmissionDataProvider";
import { isRfxStatusMatch } from "../utils";
import { useClientAccountPaths } from "newui/utils/navigation";
import { useCurrentCompany } from "hooks/auth";

const TAB_NAMES = [
	"Rfx Details",
	"Documentation",
	"Returnable Schedules",
	"Quote",
	"RFx Participants",
];

const CreateRFQPage = () => {
	const [selectedTab, setSelectedTab] = useState(1);
	const { rfxData, rfxId, updateLocalRfxData } = useRfxSubmissionData();
	// const [rfxData, setRFxData] = useState(null);

	const tabButtonList = getTabLists(false, rfxData?.rfxType);
	const formikRef = useRef();
	const [isSubmittingForm, setIsSubmittingForm] = useState(false);
	const [isValidForm, setIsValidForm] = useState(true);
	const [rfxTypes, setRFxTypes] = useState([]);
	const [documentCategories, setDocumentCategories] = useState([]);
	const [isNotFound, setIsNotFound] = useState(false);
	const [completedSteps, setCompletedSteps] = useState([]);
	const company = useCurrentCompany();
	const company_id = company?.id;
	const [touchedTabs, setTouchedTabs] = useState([]);
	const [isFormInitialized, setIsFormInitialized] = useState(false);
	const clientPaths = useClientAccountPaths();

	const { onOpen, onClose, isOpen } = useDisclosure();
	const {
		onOpen: onApprovalOpen,
		onClose: onApprovalClose,
		isOpen: isApprovalOpen,
	} = useDisclosure();

	const activeTab = tabButtonList.find((tab) => tab.value === selectedTab);
	const lastStep = selectedTab === 5;
	const [openModal, setOpenModal] = useState(false);
	const enableOnChangeFormik = selectedTab !== 3;

	const { uploadInProgresses } = useContext(RfxContext);
	const isFileUploading = uploadInProgresses?.rfxProcurer?.length > 0;

	useRefreshWarning(isFileUploading);

	const tabRef = useRef(null);

	const [staticOptionsLoaded, setStaticOptionsLoaded] = useState(false);

	useEffect(() => {
		if (!rfxData) {
			return;
		}

		// if RFx is not in draft, redirect to the view screen
		if (!isRfxStatusMatch(rfxData.status, RFX_STATUS.DRAFT)) {
			navigate(clientPaths.viewRfx(rfxData, "all"));
		}
	}, [clientPaths, rfxData]);

	// Fetch static options ONCE on mount
	useEffect(() => {
		let isMounted = true;
		setStaticOptionsLoaded(false);
		Promise.all([getRFQType(), getDocumentType()]).then(
			([types, docs, units]) => {
				if (!isMounted) return;
				setRFxTypes(types ?? []);
				setDocumentCategories(
					docs?.map((doc) => ({ label: doc.name, value: doc.id })) || []
				);
				setStaticOptionsLoaded(true);
			}
		);
		return () => {
			isMounted = false;
		};
	}, []);

	const resetRfxData = useCallback(
		(rfx) => {
			if (!rfx) {
				setIsNotFound(true);
				return;
			}

			updateLocalRfxData(rfx);
			const formValues = formValuesGenerator(rfx);
			const isFormikLoaded = formikRef.current !== undefined;

			const mergedValues = isFormikLoaded
				? mergeCreateRFxFormData(
						formValues,
						selectedTab,
						formikRef.current.values
					)
				: formValues;

			formikRef?.current?.setValues(mergedValues);
		},
		[formikRef, selectedTab, updateLocalRfxData]
	);

	const handleSave = useCallback(
		// additional message is outside the form
		async (additional_message, type) => {
			const processContinueButton = (type) => {
				switch (type) {
					case "next":
						const currentIndex = tabButtonList.findIndex(
							(item) => item.value === selectedTab
						);
						const nextIndex = currentIndex + 1;
						if (tabButtonList?.length > nextIndex) {
							changeTab(tabButtonList[nextIndex]);
						}
						break;
					case "submit":
						navigate(clientPaths.viewRfx(rfxData, "all"));
						break;
					default:
						break;
				}
			};

			const values = formikRef?.current?.values;

			try {
				setIsSubmittingForm(true);
				// calculate document_ids regardless of template status
				const document_ids =
					values?.documents
						?.filter((doc) => !doc.isRemove) // Only include non-removed documents
						.map((doc) => doc.id)
						.filter(Boolean) || [];

				const submissionFormat = processFormValuesForSaving(
					additional_message,
					null,
					values
				);

				// Clean up explicitly undefined fields
				Object.keys(submissionFormat).forEach((key) => {
					if (submissionFormat[key] === undefined) {
						delete submissionFormat[key];
					}
				});

				const res = await saveDraftRFQ(
					rfxId,
					submissionFormat,
					selectedTab,
					lastStep && openModal
				);

				if (res) {
					resetRfxData(res);
					processContinueButton(type);
				} else {
					formikRef?.current?.submitForm();
					lastStep && openModal && setTouchedTabs([1, 2, 3, 4, 5]);
					setOpenModal(false);
				}
				return true;
			} catch (e) {
				logErrorIfDebug("Error during handleSave:", e);
				setIsSubmittingForm(false);
				return false;
			} finally {
				setIsSubmittingForm(false);
			}
		},
		[
			clientPaths,
			lastStep,
			openModal,
			rfxId,
			rfxData,
			selectedTab,
			resetRfxData,
			tabButtonList,
		]
	);

	const handleSubmitOutside = async (type, additional_message) => {
		if (type === "laststep") {
			setOpenModal(true);
		} else {
			let forceSave = selectedTab === 1 || selectedTab === 2;
			checkIsCurrentStepValid(null, null, null, false).then((isValid) => {
				if (forceSave || isValid) {
					handleSave(additional_message, type);
				} else {
					const tabName = TAB_NAMES[selectedTab - 1];
					showToast(
						`Complete all required fields`,
						`${tabName} Not Saved`,
						false,
						tabName
					);
				}
			});
		}
	};

	useEffect(() => {
		formikRef?.current?.validateForm();
		setTouchedTabs((prev) => {
			if (prev.includes(selectedTab)) {
				return prev;
			} else {
				return [...prev, selectedTab];
			}
		});
	}, [selectedTab]);

	useEffect(() => {
		if (!isSubmittingForm) {
			return;
		}

		formikRef?.current?.validateForm().then((errors) => {
			Object.keys(errors).forEach((fieldName) => {
				formikRef?.current?.setFieldTouched(fieldName, true);
			});
		});
	}, [isSubmittingForm]);

	useEffect(() => {
		const formValues = formValuesGenerator(rfxData, false);

		setCompletedSteps(
			[
				formValues?.type !== "" &&
				formValues?.address.value !== "" &&
				formValues?.deadline_date !== "" &&
				formValues?.deadline_time !== ""
					? 1
					: null,
				formValues?.documents.length > 0 ? 2 : null,
				formValues?.sections.filter((val) => val?.name !== "").length > 0
					? 3
					: formValues?.returnable_schedule_file
						? 3
						: null,
				formValues?.quote_type !== "file" &&
				formValues?.quote.filter((val) => val?.id !== null).length > 0
					? 4
					: formValues?.quote_file
						? 4
						: null,
				formValues?.guest.length > 0 || formValues?.suppliers.length > 0
					? 5
					: null,
			].filter((value) => value !== null)
		);

		if (rfxData && !isFormInitialized) {
			if (
				rfxData?.status &&
				rfxData?.status.toUpperCase() === RFX_STATUS.EVALUATION
			) {
				navigate(clientPaths.rfx.all);
				return;
			}
			formikRef?.current?.setValues(formValues);

			setIsFormInitialized(true);
		}
	}, [clientPaths, rfxData, company_id, isFormInitialized]);

	const changeTab = async (tab) => {
		setSelectedTab(tab.value);
	};

	const onBackButtonClicked = () => {
		const currentIndex = tabButtonList.findIndex(
			(item) => item.value === selectedTab
		);
		const prevIndex = currentIndex - 1;
		if (prevIndex >= 0) {
			changeTab(tabButtonList[prevIndex]);
		}
		tabRef?.current?.scrollIntoView();
	};

	const handleTabChangeWithSave = async (tab) => {
		if (isFileUploading) {
			return;
		}

		validateOnSave(() => changeTab(tab));
	};

	const validateOnSave = (onSuccess) => {
		// check current tab - if RfxDetail or documentaton, we want to enforce saving (regardless of validation result)
		let forceSave = selectedTab === 1 || selectedTab === 2;

		checkIsCurrentStepValid(null, selectedTab, null, false).then((isValid) => {
			if (forceSave || isValid) {
				// save and then change tab
				handleSave(undefined, "tabchange").then((result) => {
					// if enforce saving, ensure saved successfully
					((forceSave && result) || !forceSave) && onSuccess();
				});
			} else {
				const tabName = TAB_NAMES[selectedTab - 1];
				showToast(
					`You must complete all required fields.`,
					`${tabName} Not Saved`,
					false,
					tabName
				);
				onSuccess();
			}
		});
	};

	const collectTouchedPaths = (values, basePath = "") => {
		const paths = [];

		if (!values) {
			return paths;
		}

		// if answer is an array of values
		if (Array.isArray(values)) {
			// if we are at the base of the array and its empty or not an array of objects
			// mark the general field
			if (values.length === 0 || typeof values[0] !== "object") {
				// mark the array itself
				paths.push(basePath);
			}

			// now process each value in the array
			values.forEach((item, index) => {
				// if an objecttype, we need to keep going through the path to get to the 'value'
				if (typeof item === "object" && item !== null) {
					paths.push(...collectTouchedPaths(item, `${basePath}[${index}]`));
				} else {
					// primitive value: push the indexed path
					paths.push(`${basePath}[${index}]`);
				}
			});
		} else if (typeof values === "object") {
			Object.entries(values).forEach(([key, val]) => {
				const path = basePath ? `${basePath}.${key}` : key;

				if (key === "fields" && values.type === 10) {
					paths.push(path);

					if (val?.allow_document_categories) {
						paths.push(`${path}.allow_document_categories`);
					}
				} else {
					paths.push(path);
				}

				paths.push(...collectTouchedPaths(val, path));
			});
		}

		return paths;
	};

	// Helper function to recursively touch all fields in a nested structure
	const touchFieldsRecursively = (values, basePath, formik) => {
		const pathsToTouch = collectTouchedPaths(values, basePath);
		pathsToTouch.forEach((path) => formik.setFieldTouched(path, true, false));
	};

	const checkIsCurrentStepValid = async (
		handleOnStepValid,
		value,
		type,
		showValidationError = true
	) => {
		const currentForm = formikRef?.current;

		try {
			// Add small delay to ensure spinner appears --> Returnable schedules could be very long, treated specially
			if (selectedTab === 3) {
				await new Promise((resolve) => setTimeout(resolve, 100));
			}

			const errors = await currentForm?.validateForm();

			if (selectedTab === 3) {
				await new Promise((resolve) => {
					touchFieldsRecursively(
						currentForm.values.sections,
						"sections",
						currentForm
					);
					resolve();
				});
			} else {
				Object.keys(errors).forEach((fieldName) => {
					currentForm.setFieldTouched(fieldName, true);
				});
			}

			const updatedErrors = await currentForm.validateForm();

			const validationResult = validStepChecker(
				selectedTab,
				updatedErrors,
				false
			);

			const isCurrentStepValid =
				validationResult.isValid && touchedTabs.includes(selectedTab);

			if (isCurrentStepValid) {
				handleOnStepValid && (await handleOnStepValid(value, type));
			} else {
				showValidationError &&
					showToast(validationResult.errorMessage, "Error", false);
			}

			return isCurrentStepValid;
		} catch (error) {
			logErrorIfDebug("Error during validation:", error);
			showToast("An error occurred during validation", "Error", false);
		} finally {
			setIsSubmittingForm(false);
		}
	};

	const onRfxTypeChange = (e) => {
		const type = e ? rfxTypes.find((type) => type.code === e.value) : null;
		updateLocalRfxData({ ...rfxData, rfxType: type });
	};

	const hasLoaded = staticOptionsLoaded && rfxData;

	let blockSendingRFx = lastStep
		? !isValidForm || isSubmittingForm || isFileUploading
		: isSubmittingForm || isFileUploading;

	return (
		<>
			{isNotFound && <NotFound />}
			{!isNotFound && (
				<div className="relative pt-4">
					<div ref={tabRef}>
						<CreateRFxHeaderButtons
							company_id={company_id}
							disableLastStep={blockSendingRFx}
							handleSubmitOutside={handleSubmitOutside}
							isEdit={true}
							isLoading={!hasLoaded}
							isFileUploading={isFileUploading}
							isSubmittingForm={isSubmittingForm}
							isTemplate={false}
							isTemplateQuoteFormInvalid={false}
							isValidForm={isValidForm}
							lastStep={lastStep}
							loadRFQData={rfxData}
							onOpen={onOpen}
							onOpenApproval={onApprovalOpen}
							rfqData={rfxData}
						/>
					</div>
					<div
						id="create-rfq-content"
						className="p-6"
					>
						<RFQTabNavigation
							isLoading={!hasLoaded}
							tabButtonList={tabButtonList}
							selectedTab={selectedTab}
							completedSteps={completedSteps}
							touchedTabs={touchedTabs}
							formikRef={formikRef}
							isTemplate={false}
							isEdit={true}
							rfqData={rfxData}
							onChangeTab={handleTabChangeWithSave}
						/>

						{<SavingOverlay isShown={isSubmittingForm} />}

						{hasLoaded ? (
							<Formik
								onSubmit={() => {}}
								initialValues={formValuesGenerator(rfxData, false)}
								innerRef={formikRef}
								validationSchema={validationSchema(false, rfxData?.rfxType)}
								validateOnChange={enableOnChangeFormik}
								validateOnMount={true}
							>
								{({
									isSubmitting,
									isValid,
									errors,
									values,
									setFieldError,
									setFieldValue,
								}) => {
									blockSendingRFx = blockSendingRFx || (!isValid && lastStep);
									if (isValid !== isValidForm) {
										setIsValidForm(isValid);
									}
									return (
										<Form>
											{selectedTab === 1 && (
												<ProjectDetail
													titlePage={activeTab.title}
													subTitlePage={activeTab.subTitle}
													rfxData={rfxData}
													rfxTypes={rfxTypes}
													onRfxTypeChange={onRfxTypeChange}
												/>
											)}
											{selectedTab === 2 && (
												<Documentation
													titlePage={activeTab.title}
													subTitlePage={activeTab.subTitle}
													documentCategories={documentCategories}
													isTemplate={false}
													rfxOrRfxTemplateId={rfxId}
												/>
											)}
											{selectedTab === 3 && (
												<ReturnableSchedule
													titlePage={activeTab.title}
													subTitlePage={activeTab.subTitle}
													setFieldError={setFieldError}
													setFieldValue={setFieldValue}
													isTemplate={false}
													rfxOrRfxTemplateId={rfxId}
													previewRfxHandler={onOpen}
												/>
											)}
											{selectedTab === 4 && (
												<Quote
													titlePage={activeTab.title}
													subTitlePage={activeTab.subTitle}
													setFieldValue={setFieldValue}
													isTemplate={false}
													rfxOrRfxTemplateId={rfxId}
												/>
											)}
											{lastStep && (
												<Suppliers
													rfqId={rfxId}
													showErrorMessage={false}
												/>
											)}
											<Flex
												gap={4}
												align={"center"}
												className="float-end py-10"
											>
												<TextButton
													disabled={isSubmittingForm || isFileUploading}
													onClick={() => handleSubmitOutside("save")}
												>
													Save Draft
												</TextButton>
												{selectedTab > 1 && (
													<Button
														disabled={isSubmittingForm || isFileUploading}
														btntype={"base"}
														type="button"
														onClick={onBackButtonClicked}
													>
														Back
													</Button>
												)}
												<ContinueButton
													onClick={() =>
														handleSubmitOutside(lastStep ? "laststep" : "next")
													}
													disabled={blockSendingRFx}
													isTemplate={false}
													lastStep={lastStep}
												/>
											</Flex>
											{isOpen && (
												<PreviewRFQ
													isOpen={isOpen}
													onClose={onClose}
													rfqData={rfxData}
													documentCategories={documentCategories}
													rfqTypes={rfxTypes}
													isTemplate={false}
												/>
											)}

											<SubmitRFQModal
												isOpen={openModal}
												setIsOpen={setOpenModal}
												rfqId={rfxId}
												rfqDetail={rfxData}
												isSaving={isSubmittingForm}
												companyName={company?.name}
												isSubmitting={isSubmitting}
												handleSubmit={handleSubmitOutside}
											/>
										</Form>
									);
								}}
							</Formik>
						) : (
							<div className="min-h-[70vh]">
								<LoadingSpinner label={"Loading..."} />
							</div>
						)}

						<InvitePreApprovalDrawer
							rfqName={rfxData?.name}
							onClose={onApprovalClose}
							isOpen={isApprovalOpen}
							rfx={rfxData}
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default CreateRFQPage;
